import {
  SLOT_LINES,
  SLOT_PAYTABLE,
  SLOT_SYMBOLS,
  slotsWalletCapApplied,
  type SlotRound,
  type SlotSymbol,
  type SlotsSpin,
} from "@isle/domain";
import { PortalApiError, type PortalApiClient } from "./api-client.js";
import type { RouletteConfigurationSummary } from "./types.js";

export type SlotsSpinSummary = Omit<
  SlotsSpin,
  "createdAt" | "userId" | "requestId"
> & { createdAt: string };
export interface SlotsState {
  configuration: RouletteConfigurationSummary;
  balance: number;
  recentSpins: SlotsSpinSummary[];
}
export interface SlotsResult {
  spin: SlotsSpinSummary;
  balance: number;
}
const labels: Record<SlotSymbol, string> = {
  rex: "Tyrannosaurus",
  raptor: "Raptor",
  trike: "Triceratops",
  amber: "Amber",
  fern: "Fern",
  egg: "Dino egg",
  wild: "Claw wild",
  fossil: "Fossil scatter",
};
const fmt = (value: number) => value.toLocaleString("en-GB");
const escape = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ]!,
  );
export const slotSymbol = (symbol: SlotSymbol) =>
  `<span class="slot-symbol slot-symbol-${symbol}" role="img" aria-label="${labels[symbol]}" style="--sx:${(SLOT_SYMBOLS.indexOf(symbol) % 3) * 50}%;--sy:${Math.floor(SLOT_SYMBOLS.indexOf(symbol) / 3) * 50}%"></span>`;
const initialReels: SlotSymbol[][] = [
  ["fern", "rex", "amber"],
  ["egg", "raptor", "fern"],
  ["wild", "trike", "fossil"],
  ["amber", "rex", "egg"],
  ["trike", "raptor", "fern"],
];

export function renderSlots(state: SlotsState): string {
  const config = state.configuration;
  const last = state.recentSpins[0];
  const reels = last?.rounds.at(-1)?.reels ?? initialReels;
  return `<section class="fossil-fortune" aria-label="Fossil Fortune slots">
    <div class="slot-atmosphere" aria-hidden="true"><span></span><span></span><span></span></div>
    <header class="slot-heading"><div><span class="slot-kicker">THE NEST ORIGINALS · VOL. 01</span><h2>FOSSIL <em>FORTUNE</em></h2><p>Deep in the vault. Beyond extinction.</p></div><div class="slot-view-controls"><button type="button" class="slot-sound" data-slot-action="theatre" aria-pressed="false">Expand game</button><button type="button" class="slot-sound" data-slot-action="sound" aria-pressed="false" aria-label="Enable slot sounds">Sound off</button></div></header>
    <div class="slot-feature-rail"><span>${slotSymbol("fossil")}<b>3+ FOSSILS<small>Unlock Fossil Hunt</small></b></span><span>${slotSymbol("wild")}<b>CLAW WILDS<small>Boost free-spin multipliers</small></b></span><span class="slot-max"><b>1,000×<small>Maximum total return</small></b></span></div>
    <div class="slot-machine" data-slot-machine>
      <div class="slot-machine-top"><span data-slot-mode>10 PAYLINES · ALL ACTIVE</span><span data-slot-multiplier>BASE GAME · 1×</span></div>
      <div class="slot-reel-window"><div class="slot-reels" data-slot-reels>${reels.map((reel, i) => `<div class="slot-reel" data-slot-reel="${i}"><div class="slot-strip">${reel.map(slotSymbol).join("")}</div></div>`).join("")}</div><svg class="slot-paylines" viewBox="0 0 500 300" preserveAspectRatio="none" aria-hidden="true" data-slot-lines></svg></div>
      <div class="slot-overlay" data-slot-overlay hidden></div>
      <div class="slot-status" role="status" aria-live="polite" data-slot-status>${config.enabled ? "Choose your stake. Unearth your fortune." : "The vault is closed. Check back when the game reopens."}</div>
    </div>
    <div class="slot-console"><div class="slot-meter"><small>BALANCE · STICKS</small><strong data-slot-balance>${fmt(state.balance)}</strong></div><div class="slot-stake"><label for="slotWager">TOTAL STAKE · STICKS</label><div><button type="button" data-slot-action="less" aria-label="Decrease stake">−</button><input id="slotWager" data-slot-wager type="number" inputmode="numeric" min="${config.minimumWager}" max="${config.maximumWager}" step="${config.wagerStep}" value="${config.minimumWager}" /><button type="button" data-slot-action="more" aria-label="Increase stake">+</button></div></div><button type="button" class="slot-spin" data-slot-action="spin"${!config.enabled || state.balance < config.minimumWager ? " disabled" : ""}><span>↻</span><b>SPIN<small>STICKS</small></b></button><div class="slot-meter slot-return"><small>TOTAL RETURN</small><strong data-slot-return>0</strong></div></div>
    <div class="slot-underbar"><span>${fmt(config.minimumWager)}–${fmt(config.maximumWager)} Sticks · ${fmt(config.dailyWagerLimit)} daily stake limit</span><button type="button" data-slot-action="skip" hidden>Skip animation</button><button type="button" data-slot-action="replay"${last ? "" : " hidden"}>Replay last result</button></div>
    <div class="slot-information"><details><summary>How to play & paytable <span>↗</span></summary><p>Each stake covers all ten lines. Match 3, 4 or 5 symbols consecutively from the left. Only the highest combination on each line pays. The table shows multiples of the line stake (total stake ÷ 10); returns include any stake returned.</p><div class="slot-paytable"><span>SYMBOL</span><b>3</b><b>4</b><b>5</b>${Object.entries(
      SLOT_PAYTABLE,
    )
      .map(
        ([symbol, pays]) =>
          `<span>${slotSymbol(symbol as SlotSymbol)}${labels[symbol as SlotSymbol]}</span>${pays.map((pay) => `<b>${pay}×</b>`).join("")}`,
      )
      .join(
        "",
      )}</div><div class="slot-line-guide">${SLOT_LINES.map((rows, i) => `<span><small>${i + 1}</small><svg viewBox="0 0 100 60" aria-label="Payline ${i + 1}: rows ${rows.map((row) => row + 1).join(", ")}"><polyline points="${rows.map((row, col) => `${10 + col * 20},${10 + row * 20}`).join(" ")}" /></svg></span>`).join("")}</div><p><strong>Fossil Hunt:</strong> 3 / 4 / 5+ scatters anywhere award 8 / 10 / 12 free spins. Free spins start at 2×; any claw wild raises the next free spin's multiplier by one, up to 5×. Three or more scatters during a free spin add 3 spins, up to 20 free spins total. Wilds replace any paying symbol, never scatters. Scatters award free spins only. The full paid spin and bonus stop at a combined 1,000× return.</p><p><strong>Wallet cap:</strong> Winnings, including free spins, are limited to the space in your wallet after the stake is deducted. Any excess is not credited. Your daily wager limit is separate.</p><p>Every cell is drawn independently. Symbol weights out of 100: Rex 7, Raptor 10, Triceratops 14, Amber 18, Fern 24, Egg 21, Wild 3, Fossil 3. Sticks have no cash value. Outcomes are independent of previous spins.</p></details><details><summary>Your recent spins <span>↗</span></summary><div data-slot-history>${renderSlotHistory(state.recentSpins)}</div></details></div>
  </section>`;
}
function renderSlotHistory(spins: SlotsSpinSummary[]): string {
  return spins.length
    ? `<ol class="slot-history">${spins.map((spin) => `<li><div><strong>${spin.rounds.length > 1 ? `Fossil Hunt · ${spin.rounds.length - 1} free spins` : "Base spin"}</strong><small>${escape(new Date(spin.createdAt).toLocaleString("en-GB"))} · ${fmt(spin.wager)} staked</small></div><span>${fmt(spin.payout)} returned<small>Net ${spin.payout >= spin.wager ? "+" : ""}${fmt(spin.payout - spin.wager)} Sticks${slotsWalletCapApplied(spin) ? " · Wallet cap applied" : ""}</small></span></li>`).join("")}</ol>`
    : "<p>Your settled spins will appear here.</p>";
}

export async function mountSlots(
  root: HTMLElement,
  api: PortalApiClient,
  userId: string,
  serverId: string,
  onBalance: (balance: number) => void,
): Promise<void> {
  let state: SlotsState;
  try {
    state = await api.getSlots(userId, serverId);
  } catch (error) {
    root.innerHTML = `<section class="panel"><h3>The vault could not connect</h3><p>${escape(error instanceof Error ? error.message : String(error))}</p><button type="button" data-slot-retry>Reconnect</button></section>`;
    root
      .querySelector("[data-slot-retry]")
      ?.addEventListener(
        "click",
        () => void mountSlots(root, api, userId, serverId, onBalance),
        { once: true },
      );
    return;
  }
  if (!root.isConnected) return;
  root.innerHTML = renderSlots(state);
  onBalance(state.balance);
  const q = <T extends HTMLElement>(selector: string) =>
    root.querySelector<T>(selector)!;
  const status = (message: string) => {
    q("[data-slot-status]").textContent = message;
  };
  const stake = q<HTMLInputElement>("[data-slot-wager]");
  const storageKey = `nest-slots-pending:${serverId}:${userId}`;
  let pending: { requestId: string; wager: number } | undefined;
  try {
    const value = JSON.parse(localStorage.getItem(storageKey) ?? "null");
    if (
      typeof value?.requestId === "string" &&
      Number.isSafeInteger(value.wager)
    )
      pending = value;
  } catch {
    /* Storage is optional. */
  }
  let busy = false,
    skip = false,
    sound = false,
    audio: AudioContext | undefined;
  let continueBonus: (() => void) | undefined;
  const reducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const tone = (frequency: number, duration = 0.07) => {
    if (!sound) return;
    try {
      audio ??= new AudioContext();
      void audio.resume();
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      oscillator.connect(gain);
      gain.connect(audio.destination);
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.025, audio.currentTime);
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        audio.currentTime + duration,
      );
      oscillator.start();
      oscillator.stop(audio.currentTime + duration);
    } catch {
      /* Sound never blocks a spin. */
    }
  };
  const controls = () => {
    stake.disabled = busy || Boolean(pending);
    root
      .querySelectorAll<HTMLButtonElement>(
        '[data-slot-action="less"],[data-slot-action="more"],[data-slot-action="replay"]',
      )
      .forEach((button) => (button.disabled = busy || Boolean(pending)));
    const spin = q<HTMLButtonElement>('[data-slot-action="spin"]');
    spin.disabled =
      busy ||
      (!pending &&
        (!state.configuration.enabled || Number(stake.value) > state.balance));
    spin.querySelector("b")!.innerHTML =
      `${pending ? "RECOVER" : "SPIN"}<small>${pending ? "SAME SPIN" : "STICKS"}</small>`;
    q<HTMLButtonElement>('[data-slot-action="skip"]').hidden = !busy;
  };
  if (pending) {
    stake.value = String(pending.wager);
    status(
      "A spin needs confirmation. Recover checks the same request and cannot charge twice.",
    );
  }
  controls();
  const pause = async (ms: number) => {
    const end = Date.now() + ms;
    while (!skip && root.isConnected && Date.now() < end)
      await new Promise((resolve) => setTimeout(resolve, 40));
  };
  async function animateRound(round: SlotRound): Promise<void> {
    q("[data-slot-lines]").innerHTML = "";
    const fast = skip || reducedMotion();
    q("[data-slot-mode]").textContent =
      round.mode === "free"
        ? "FOSSIL HUNT · FREE SPIN"
        : "10 PAYLINES · ALL ACTIVE";
    q("[data-slot-multiplier]").textContent =
      `${round.multiplier}× WIN MULTIPLIER`;
    await Promise.all(
      round.reels.map(async (reel, i) => {
        const holder = q(`[data-slot-reel="${i}"]`);
        holder.classList.remove("is-win");
        const filler = Array.from(
          { length: 24 },
          (_, j) => SLOT_SYMBOLS[(j + i * 3) % SLOT_SYMBOLS.length]!,
        );
        holder.innerHTML = `<div class="slot-strip">${[...filler, ...reel].map(slotSymbol).join("")}</div>`;
        const strip = holder.firstElementChild as HTMLElement;
        const height = holder.clientHeight / 3;
        const animation = strip.animate(
          [
            { offset: 0, transform: "translateY(0)", filter: "blur(1px)" },
            { offset: 0.08, transform: `translateY(-${height}px)`, filter: "blur(3px)" },
            { offset: 0.72, transform: `translateY(-${height * 20}px)`, filter: "blur(3px)", easing: "cubic-bezier(.15,.7,.25,1)" },
            { offset: 1, transform: `translateY(-${height * 24}px)`, filter: "blur(0)" },
          ],
          {
            duration: fast ? 0 : 2900 + i * 400,
            easing: "linear",
            fill: "forwards",
          },
        );
        const finish = setInterval(() => {
          if (skip || !root.isConnected) animation.finish();
        }, 40);
        await animation.finished.catch(() => {});
        clearInterval(finish);
        holder.innerHTML = `<div class="slot-strip">${reel.map(slotSymbol).join("")}</div>`;
        animation.cancel();
        if (root.isConnected) tone(200 + i * 80);
      }),
    );
    if (!root.isConnected) return;
    q("[data-slot-lines]").innerHTML = round.wins
      .map(
        (win) =>
          `<polyline points="${SLOT_LINES[win.line]!.slice(0, win.count)
            .map((row, i) => `${50 + i * 100},${50 + row * 100}`)
            .join(" ")}" />`,
      )
      .join("");
    for (const win of round.wins)
      SLOT_LINES[win.line]!.slice(0, win.count).forEach((row, i) =>
        q(`[data-slot-reel="${i}"]`).children[0]?.children[row]?.classList.add(
          "is-winning-symbol",
        ),
      );
  }
  async function present(
    spin: SlotsSpinSummary,
    replay = false,
  ): Promise<void> {
    busy = true;
    skip = false;
    controls();
    let shownPayout = 0;
    for (let index = 0; index < spin.rounds.length; index++) {
      if (!root.isConnected) break;
      const round = spin.rounds[index]!;
      q(".fossil-fortune").classList.toggle(
        "is-fossil-hunt",
        round.mode === "free",
      );
      status(
        round.mode === "free"
          ? `Free spin ${index} · ${round.multiplier}× multiplier`
          : replay
            ? "Replaying a settled result. No Sticks spent."
            : "The vault is turning…",
      );
      await animateRound(round);
      const creditedRound = Math.max(0, Math.min(round.payout, spin.payout - shownPayout));
      shownPayout += creditedRound;
      q("[data-slot-return]").textContent = fmt(shownPayout);
      status(
        round.payout
          ? `${fmt(creditedRound)} Sticks returned${creditedRound < round.payout ? " · Wallet cap applied" : ""}${round.freeSpinsAdded ? ` · ${round.freeSpinsAdded} free spins awarded` : ""}`
          : round.freeSpinsAdded
            ? `${round.freeSpinsAdded} free spins unlocked!`
            : "No line return on this spin.",
      );
      if (round.mode === "base" && spin.rounds.length > 1 && !skip) {
        const overlay = q("[data-slot-overlay]");
        overlay.hidden = false;
        overlay.innerHTML = `<div class="slot-bonus-reveal">${slotSymbol("fossil")}<small>YOU UNEARTHED</small><h3>FOSSIL HUNT</h3><strong>${round.freeSpinsAdded} FREE SPINS</strong><p>Start at 2×. Claw wilds raise the multiplier.</p><button type="button" data-slot-action="continue">Enter the vault →</button></div>`;
        overlay
          .querySelector<HTMLButtonElement>("button")
          ?.focus({ preventScroll: true });
        tone(660, 0.4);
        await new Promise<void>((resolve) => {
          const finish = () => {
            clearInterval(watch);
            resolve();
          };
          const watch = setInterval(() => {
            if (!root.isConnected || skip) finish();
          }, 100);
          continueBonus = finish;
        });
        overlay.hidden = true;
        continueBonus = undefined;
      } else await pause(reducedMotion() ? 150 : 950);
    }
    if (!root.isConnected) return;
    q(".fossil-fortune").classList.remove("is-fossil-hunt");
    const profit = spin.payout - spin.wager;
    status(
      `${replay ? "Replay · " : ""}${fmt(spin.payout)} Sticks returned · Net ${profit >= 0 ? "+" : ""}${fmt(profit)}${slotsWalletCapApplied(spin) ? " · Wallet cap applied" : ""}${spin.capped ? " · Maximum win reached" : ""}`,
    );
    if (profit > 0 && !skip) {
      q("[data-slot-return]").animate(
        [
          { transform: "scale(1)" },
          { transform: "scale(1.2)" },
          { transform: "scale(1)" },
        ],
        { duration: reducedMotion() ? 0 : 700 },
      );
      tone(880, 0.3);
    }
    if (!skip && (spin.rounds.length > 1 || profit >= spin.wager * 10)) {
      const overlay = q("[data-slot-overlay]");
      overlay.hidden = false;
      overlay.innerHTML = `<div class="slot-bonus-reveal slot-bonus-complete">${slotSymbol(spin.rounds.length > 1 ? "fossil" : "rex")}<small>${spin.rounds.length > 1 ? "FOSSIL HUNT COMPLETE" : "APEX FIND"}</small><h3>${fmt(spin.payout)} STICKS</h3><strong>Net ${profit >= 0 ? "+" : ""}${fmt(profit)}</strong><p>${spin.rounds.length > 1 ? `${spin.rounds.length - 1} free spins · ` : ""}Your result is saved.${slotsWalletCapApplied(spin) ? " Wallet cap applied; excess winnings were not credited." : ""}</p><button type="button" data-slot-action="skip">Back to reels →</button></div>`;
      overlay
        .querySelector<HTMLButtonElement>("button")
        ?.focus({ preventScroll: true });
      await pause(reducedMotion() ? 1500 : 3000);
      overlay.hidden = true;
    }
    busy = false;
    controls();
    q<HTMLButtonElement>('[data-slot-action="spin"]').focus({
      preventScroll: true,
    });
  }
  async function spin(): Promise<void> {
    if (busy) return;
    const wager = pending?.wager ?? Number(stake.value),
      config = state.configuration;
    if (
      !pending &&
      (!Number.isSafeInteger(wager) ||
        wager < config.minimumWager ||
        wager > config.maximumWager ||
        wager % config.wagerStep ||
        wager % 10)
    ) {
      status(
        `Choose ${fmt(config.minimumWager)}–${fmt(config.maximumWager)} Sticks in steps of ${config.wagerStep}.`,
      );
      return;
    }
    busy = true;
    pending ??= { requestId: crypto.randomUUID(), wager };
    controls();
    try {
      localStorage.setItem(storageKey, JSON.stringify(pending));
    } catch {
      /* Keep request in memory if storage is unavailable. */
    }
    status("Confirming your spin…");
    try {
      const result = await api.spinSlots({ userId, serverId, ...pending });
      pending = undefined;
      try {
        localStorage.removeItem(storageKey);
      } catch {}
      state.balance = result.balance;
      state.recentSpins = [
        result.spin,
        ...state.recentSpins.filter((spin) => spin.id !== result.spin.id),
      ].slice(0, 12);
      await present(result.spin);
      if (root.isConnected) {
        onBalance(result.balance);
        q("[data-slot-balance]").textContent = fmt(result.balance);
        q("[data-slot-history]").innerHTML = renderSlotHistory(
          state.recentSpins,
        );
        q<HTMLButtonElement>('[data-slot-action="replay"]').hidden = false;
      }
    } catch (error) {
      // Network errors and 5xx replies are ambiguous: keep exactly the same request for recovery.
      if (
        error instanceof PortalApiError &&
        error.status >= 400 &&
        error.status < 500
      ) {
        pending = undefined;
        try {
          localStorage.removeItem(storageKey);
        } catch {}
      }
      status(
        `${error instanceof Error ? error.message : "Connection interrupted."}${pending ? " Recover this spin before placing another wager." : ""}`,
      );
      busy = false;
      controls();
    }
  }
  stake.addEventListener("input", controls);
  root.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      "[data-slot-action]",
    );
    if (!button || button.disabled) return;
    const action = button.dataset.slotAction;
    if (action === "spin") void spin();
    if (action === "skip") {
      skip = true;
      continueBonus?.();
    }
    if (action === "continue") continueBonus?.();
    if (action === "sound") {
      sound = !sound;
      button.textContent = sound ? "Sound on" : "Sound off";
      button.setAttribute("aria-pressed", String(sound));
      tone(440);
    }
    if (action === "theatre") {
      const expanded = q(".fossil-fortune").classList.toggle("slot-theatre");
      button.textContent = expanded ? "Close expanded view" : "Expand game";
      button.setAttribute("aria-pressed", String(expanded));
    }
    if (action === "replay" && !busy && state.recentSpins[0])
      void present(state.recentSpins[0], true);
    if ((action === "more" || action === "less") && !busy && !pending) {
      stake.value = String(
        Math.max(
          state.configuration.minimumWager,
          Math.min(
            state.configuration.maximumWager,
            Number(stake.value) +
              (action === "more" ? 1 : -1) * state.configuration.wagerStep,
          ),
        ),
      );
      controls();
    }
  });
  root.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      q(".fossil-fortune").classList.remove("slot-theatre");
      const button = q<HTMLButtonElement>('[data-slot-action="theatre"]');
      button.textContent = "Expand game";
      button.setAttribute("aria-pressed", "false");
    }
  });
}
